// =============================================
// Layer 5 — Schema.org JSON-LD Audit
// FREE (parse + validate), ~10ms
// =============================================
// Sprawdza:
//   - Czy <script ld+json> w final_html → parse OK
//   - Czy @graph zawiera BlogPosting + WebPage (core w naszym hybrid)
//   - Czy datePublished w schema == effective_publish_date / published_at z DB
//   - BANNED tokens check (Organization / WebSite / publisher / worksFor / isPartOf):
//     nasz architecture constraint — schema generator wycięty cleanup 2026-05-18.
//     Obecność któregokolwiek = warning (potential regression).
// =============================================

const BANNED_SCHEMA_TOKENS = [
  '"Organization"',
  '"WebSite"',
  '"publisher"',
  '"worksFor"',
  '"isPartOf"',
];

export interface SchemaAuditResult {
  status: "ok" | "warning" | "failed";
  has_jsonld: boolean;
  parse_ok: boolean;
  has_blogposting: boolean;
  has_webpage: boolean;
  date_published_schema: string | null;
  date_published_match: boolean | null;
  banned_tokens_found: string[];
  issues: string[];
}

function extractJsonLd(html: string): string | null {
  const m = html.match(
    /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/i,
  );
  return m ? m[1].trim() : null;
}

function findGraphNodes(parsed: unknown): unknown[] {
  if (!parsed || typeof parsed !== "object") return [];
  const obj = parsed as Record<string, unknown>;
  if (Array.isArray(obj["@graph"])) return obj["@graph"];
  // Niektóre wersje (legacy) mogą być flat node bez @graph
  return [obj];
}

function dateMatchesAtLeastMinute(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  // Strip everything po minute granularity: "2026-05-04T10:30"
  const norm = (s: string) => s.replace(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}).*/, "$1");
  return norm(a) === norm(b);
}

export function auditSchema(
  finalHtml: string,
  expectedDatePublished: string | null,
): SchemaAuditResult {
  const issues: string[] = [];
  const jsonldRaw = extractJsonLd(finalHtml);

  if (!jsonldRaw) {
    return {
      status: "failed",
      has_jsonld: false,
      parse_ok: false,
      has_blogposting: false,
      has_webpage: false,
      date_published_schema: null,
      date_published_match: null,
      banned_tokens_found: [],
      issues: ["Brak <script ld+json> w final_html"],
    };
  }

  // BANNED tokens check — RAW string match, niezależnie od JSON parse
  const banned_tokens_found: string[] = [];
  for (const token of BANNED_SCHEMA_TOKENS) {
    if (jsonldRaw.includes(token)) {
      banned_tokens_found.push(token.replace(/"/g, ""));
    }
  }

  // Parse JSON
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonldRaw);
  } catch (err) {
    return {
      status: "failed",
      has_jsonld: true,
      parse_ok: false,
      has_blogposting: false,
      has_webpage: false,
      date_published_schema: null,
      date_published_match: null,
      banned_tokens_found,
      issues: [
        `JSON parse failed: ${err instanceof Error ? err.message : "unknown"}`,
        ...(banned_tokens_found.length > 0
          ? [`Banned tokens detected: ${banned_tokens_found.join(", ")}`]
          : []),
      ],
    };
  }

  const nodes = findGraphNodes(parsed);
  let blogPosting: Record<string, unknown> | null = null;
  let webPage: Record<string, unknown> | null = null;

  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    const type = (node as Record<string, unknown>)["@type"];
    if (type === "BlogPosting" || (Array.isArray(type) && type.includes("BlogPosting"))) {
      blogPosting = node as Record<string, unknown>;
    }
    if (type === "WebPage" || (Array.isArray(type) && type.includes("WebPage"))) {
      webPage = node as Record<string, unknown>;
    }
  }

  const has_blogposting = !!blogPosting;
  const has_webpage = !!webPage;

  if (!has_blogposting) issues.push("Brak BlogPosting node w @graph");
  if (!has_webpage) issues.push("Brak WebPage node w @graph");

  const date_published_schema =
    blogPosting && typeof blogPosting.datePublished === "string"
      ? (blogPosting.datePublished as string)
      : null;

  let date_published_match: boolean | null = null;
  if (expectedDatePublished && date_published_schema) {
    date_published_match = dateMatchesAtLeastMinute(date_published_schema, expectedDatePublished);
    if (!date_published_match) {
      issues.push(
        `datePublished mismatch: schema="${date_published_schema}" expected="${expectedDatePublished}"`,
      );
    }
  }

  if (banned_tokens_found.length > 0) {
    issues.push(`Banned schema tokens: ${banned_tokens_found.join(", ")}`);
  }

  // Status:
  //   failed = brak schema / parse fail / brak BlogPosting (critical)
  //   warning = banned tokens / date mismatch / brak WebPage
  //   ok = wszystko OK
  let status: SchemaAuditResult["status"] = "ok";
  if (!has_blogposting) {
    status = "failed";
  } else if (
    banned_tokens_found.length > 0 ||
    date_published_match === false ||
    !has_webpage
  ) {
    status = "warning";
  }

  return {
    status,
    has_jsonld: true,
    parse_ok: true,
    has_blogposting,
    has_webpage,
    date_published_schema,
    date_published_match,
    banned_tokens_found,
    issues,
  };
}
